// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * A session that ends while a page is open (it expired, someone logged it
 * out, an administrator disabled the account): the next API answer is a
 * 401. With Supabase configured, the page first tries to re-bridge — trade
 * the still-valid Supabase session for a fresh ruxd cookie — and repeats the
 * request once. Only when that fails does it go back to the login page —
 * which comes back here — instead of showing errors (S3 review M9).
 *
 * Only in server mode: AuthGate switches it on once `GET /auth/me` says so.
 * A local server with `--auth-token` also answers 401, but has no login page
 * to go to.
 */

import { loginHref } from './auth';
import { readShareToken, shareHref } from './cases';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * For a request that started at `since` (ms, `Date.now()`) and got a 401:
 * true when ruxd should take it again — a session exchange finished after
 * it started (in any tab), or this call made one.
 */
export type Rebridge = (since: number) => Promise<boolean>;

let enabled = false;
let rebridge: Rebridge | null = null;

/** Turn the redirect on (server mode) or off. */
export function setLoginRedirect(on: boolean): void {
  enabled = on;
}

/** Install (or with null, remove) the re-bridge tried before the login page. */
export function setRebridge(fn: Rebridge | null): void {
  rebridge = fn;
}

/** Whether a response with @p status from @p url means "sign in again". */
export function shouldSendToLogin(status: number, url: string, on: boolean = enabled): boolean {
  if (!on || status !== 401) return false;
  // The gate, the login page and the bridge deal with these themselves.
  return !/\/auth\/(me|login|session|logout)(\?|$)/.test(url);
}

/** Whether a request can be sent a second time (a stream body cannot). */
function replayable(init?: RequestInit): boolean {
  return !(typeof ReadableStream !== 'undefined' && init?.body instanceof ReadableStream);
}

export interface AuthAwareFetchDeps {
  fetch: Fetch;
  /** The clock `since` is measured on. */
  now: () => number;
  /** Server mode? */
  enabled: () => boolean;
  /** The re-bridge, or null with no Supabase. */
  rebridge: () => Rebridge | null;
  /** Leave for the login page. */
  toLogin: () => void;
}

/**
 * The 401 handling above for any transport: `send` runs the request, and a
 * 401 from it (on a URL `shouldSendToLogin` cares about) re-bridges and, when
 * that took and `canReplay`, sends it once more; refused again, the page
 * goes to the login page. The re-bridge itself is single-flight and skips
 * the exchange when one finished after the request started
 * (`SessionBridge.rebridgeFor`); a request refused again after it goes to
 * the login page, so a re-bridge that "works" but is not honoured cannot
 * loop here (and the login page guards the other half, `loginLoop.ts`).
 * `fetch` goes through it as {@link createAuthAwareFetch}; the panorama
 * upload's `XMLHttpRequest` (for its progress) directly.
 */
export type AuthRetry = <R extends { status: number }>(input: string, send: () => Promise<R>, canReplay?: boolean) => Promise<R>;

export function createAuthRetry(deps: Omit<AuthAwareFetchDeps, 'fetch'>): AuthRetry {
  let leaving = false;

  const tryRebridge = async (since: number): Promise<boolean> => {
    const fn = deps.rebridge();
    if (!fn) return false;
    try {
      return await fn(since);
    } catch {
      return false;
    }
  };

  return async (input, send, canReplay = true) => {
    const started = deps.now();
    let response = await send();
    if (!shouldSendToLogin(response.status, input, deps.enabled())) return response;
    if (!leaving && canReplay && (await tryRebridge(started))) {
      response = await send();
      if (!shouldSendToLogin(response.status, input, deps.enabled())) return response;
    }
    if (!leaving) {
      leaving = true;
      deps.toLogin();
    }
    return response;
  };
}

/** `fetch`, plus the 401 handling above ({@link createAuthRetry}). */
export function createAuthAwareFetch(deps: AuthAwareFetchDeps): Fetch {
  const retry = createAuthRetry(deps);
  return (input, init) => retry(input, () => deps.fetch(input, init), replayable(init));
}

/** The page's 401 handling, shared by every transport (one `leaving` for the page). */
export const authRetry: AuthRetry = createAuthRetry({
  now: () => Date.now(),
  enabled: () => enabled,
  rebridge: () => rebridge,
  toLogin: () => {
    const share = readShareToken();
    window.location.replace(share ? shareHref(share) : loginHref(window.location.pathname + window.location.search));
  },
});

/** The default transport of every client. */
export const authAwareFetch: Fetch = (input, init) => authRetry(input, () => fetch(input, init), replayable(init));
